import { useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useCustomers } from "./useCustomers";
import { samplePayload } from "../samplePayload";
import {
  API_BASE_URL,
  DEFAULT_BRANCH_ID,
  outputSchemas,
  targetPayloadTemplates,
} from "../flowBuilderConstants";
import { toAdminApiPath } from "../../internal-access/adminApiPath";
import {
  analyzePayload,
  buildMappedPayloadFromMappings,
  buildMinimalShipmentBookingPayload,
  getBlockMeta,
  getTriggerSubtitle,
  getTriggerTitle,
  inferDefaultTransformation,
} from "../flowBuilderUtils";
import type {
  FlowActionType,
  FlowStep,
  MappingSuggestion,
  OutputField,
  PanelTab,
  PayloadAnalysis,
  PayloadFormat,
  PayloadTargetTemplate,
  OutputSchema,
  OutputType,
  TransformationType,
} from "../types";

function getFlowActionType(block: string): FlowActionType {
  switch (block) {
    case "HTTP":
    case "HTTP Input":
      return "trigger.http";
    case "When a new email arrives":
      return "trigger.email";
    case "Recurrence":
      return "trigger.recurrence";
    case "Select Customer":
      return "jdx.select-customer";
    case "Create Shipment Booking":
      return "jdx.create-shipment-booking";
    case "JDX Data Loader":
      return "jdx.data-loader";
    case "Condition":
      return "logic.condition";
    case "Switch":
      return "logic.switch";
    case "Set Variable":
      return "logic.set-variable";
    case "SQL Writer":
      return "data.sql-writer";
    case "Storage Writer":
      return "data.storage-writer";
    case "Blob Reader":
      return "data.blob-reader";
    case "HTTP Response":
      return "output.http-response";
    case "HTTP Push":
      return "output.http-push";
    case "Excel Export":
      return "output.excel";
    case "JSON Output":
      return "output.json";
    case "CSV Export":
      return "output.csv";
    case "Blob Storage":
      return "output.blob";
    case "Teams Alert":
      return "notification.teams";
    case "Email Alert":
      return "notification.email";
    default:
      return "custom";
  }
}

export function useFlowBuilder() {
  const { instance, accounts } = useMsal();
  const customerState = useCustomers(instance, accounts);

  const [integrationName, setIntegrationName] = useState("bookings");
  const [triggerType, setTriggerType] = useState<"email" | "recurrence" | "http">("http");
  const [payload, setPayload] = useState(samplePayload);
  const [activeTab, setActiveTab] = useState<PanelTab>("properties");

  const [selectedStepId, setSelectedStepId] = useState<string>("trigger");

  const [customSteps, setCustomSteps] = useState<FlowStep[]>([]);
  const [hiddenDefaultStepIds, setHiddenDefaultStepIds] = useState<string[]>([]);
  const [dynamicContentOpen, setDynamicContentOpen] = useState(false);

  const [payloadMappingEnabled, setPayloadMappingEnabled] = useState(false);
  const [inputFormat, setInputFormat] = useState<PayloadFormat>("json");
  const [targetPayloadType, setTargetPayloadType] =
    useState<PayloadTargetTemplate>("shipment-booking");
  const [mapperModalOpen, setMapperModalOpen] = useState(false);
  const [manualMappings, setManualMappings] = useState<Record<string, string>>({});
  const [mappingTransformations, setMappingTransformations] =
    useState<Record<string, TransformationType>>({});
  const [mappingConstants, setMappingConstants] = useState<Record<string, string>>({});
  const [retryPolicy, setRetryPolicy] =
    useState<"none" | "fixed" | "exponential">("none");
  const [retryCount, setRetryCount] = useState(0);

  const [testBookingState, setTestBookingState] = useState({
    loading: false,
    status: "idle" as "idle" | "success" | "error",
    message: "",
    responseBody: "",
  });

  const [outputType, setOutputType] = useState<OutputType>("http");
  const [outputSchema, setOutputSchema] = useState<OutputSchema>("shipment-booking");
  const [selectedOutputFields, setSelectedOutputFields] = useState<string[]>([
    "customerReference",
    "modality",
    "loadType",
    "originPort.unLoCode",
    "destinationPort.unLoCode",
    "containers[].number",
  ]);
  const [outputPreviewOpen, setOutputPreviewOpen] = useState(false);
  const [httpMethod, setHttpMethod] = useState("POST");
  const [httpUrl, setHttpUrl] = useState("");
  const [httpAuthType, setHttpAuthType] =
    useState<"none" | "api-key" | "bearer" | "basic" | "oauth2">("none");
  const [httpAuthValue, setHttpAuthValue] = useState("");
  const [httpHeaders, setHttpHeaders] = useState("Content-Type: application/json");

  const {
    customers,
    customersLoading,
    customersError,
    selectedCustomer,
    selectedCustomerId,
    selectedEntity,
    environment,
    setEnvironment,
    customerSearch,
    setCustomerSearch,
    customerPickerOpen,
    setCustomerPickerOpen,
    filteredCustomers,
    selectCustomer,
    getAccessToken,
  } = customerState;

  const parsedPayload = useMemo(() => {
    if (triggerType !== "http") return null;

    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }, [payload, triggerType]);

  const payloadAnalysis = useMemo<PayloadAnalysis>(() => {
    if (!parsedPayload) return { variables: [], arrays: [] };
    return analyzePayload(parsedPayload);
  }, [parsedPayload]);

  const payloadVariables = payloadAnalysis.variables;
  const payloadArrays = payloadAnalysis.arrays;
  const payloadIsValid = triggerType !== "http" || Boolean(parsedPayload);
  const selectedTargetPayload = targetPayloadTemplates[targetPayloadType];

  const suggestedPayloadMappings = useMemo<MappingSuggestion[]>(() => {
    const normalize = (value: string) => {
      const stripped = value
        .toLowerCase()
        .replace("payload.", "")
        .replace(/\[\]/g, "")
        .replace(/\[[0-9]+\]/g, "");

      return stripped
        .split("")
        .filter((char: string) =>
          (char >= "a" && char <= "z") || (char >= "0" && char <= "9")
        )
        .join("");
    };

    return selectedTargetPayload.fields.map((target) => {
      const targetKey = normalize(target.path);
      const targetLast = normalize(target.path.split(".").pop() || target.path);

      const partial = payloadVariables.find((variable) => {
        const sourceKey = normalize(variable.key);
        return (
          sourceKey.endsWith(targetKey) ||
          sourceKey.endsWith(targetLast) ||
          sourceKey.includes(targetLast)
        );
      });

      const fixedSource = target.path === "branchId" || target.path === "customer.id";
      const manualSource = manualMappings[target.path];
      const source =
        fixedSource ? "" : manualSource !== undefined ? manualSource : partial?.key || "";

      return {
        target,
        source,
        confidence: fixedSource
          ? "Fixed"
          : source
            ? manualSource !== undefined
              ? "Manual"
              : "Auto"
            : "Manual",
        transformation:
          mappingTransformations[target.path] || inferDefaultTransformation(target.path),
        constantValue: mappingConstants[target.path],
      };
    });
  }, [
    payloadVariables,
    selectedTargetPayload,
    manualMappings,
    mappingTransformations,
    mappingConstants,
  ]);

  const availableOutputFields = useMemo<OutputField[]>(() => {
    if (outputSchema === "custom") {
      return payloadVariables
        .filter((variable) => !variable.value.startsWith("[array:"))
        .map((variable) => ({
          key: variable.key,
          label: variable.key.replace(/^payload\./, ""),
          group: variable.source,
        }));
    }

    return outputSchemas[outputSchema].fields;
  }, [outputSchema, payloadVariables]);

  const selectedOutputFieldRows = useMemo(
    () => availableOutputFields.filter((field) => selectedOutputFields.includes(field.key)),
    [availableOutputFields, selectedOutputFields]
  );

  const mappedBookingPayload = useMemo(() => {
    if (!parsedPayload) return null;

    if (!payloadMappingEnabled) {
      return buildMinimalShipmentBookingPayload(parsedPayload, selectedCustomerId);
    }

    return buildMappedPayloadFromMappings(
      parsedPayload,
      suggestedPayloadMappings,
      selectedCustomerId
    );
  }, [parsedPayload, selectedCustomerId, payloadMappingEnabled, suggestedPayloadMappings]);

  const bookingPayloadPreview = useMemo(() => {
    if (!mappedBookingPayload) {
      return "Invalid JSON payload. Fix the input payload to generate a test request body.";
    }

    return JSON.stringify(mappedBookingPayload, null, 2);
  }, [mappedBookingPayload]);

  const outputPreview = useMemo(() => {
    const output: Record<string, string> = {};

    selectedOutputFieldRows.forEach((field) => {
      const sourceKey = field.key.startsWith("payload.")
        ? field.key
        : "payload." + field.key;

      output[field.key] = "{{" + sourceKey + "}}";
    });

    if (outputType === "http-push") {
      const headers = httpHeaders
        .split("\n")
        .filter(Boolean)
        .reduce<Record<string, string>>((acc, row) => {
          const [key, ...rest] = row.split(":");
          if (key) acc[key.trim()] = rest.join(":").trim();
          return acc;
        }, {});

      return JSON.stringify(
        {
          method: httpMethod,
          url: httpUrl || "https://customer.example.com/webhook",
          authentication: httpAuthType,
          authValue: httpAuthValue ? "{{secret}}" : "",
          headers,
          body: output,
        },
        null,
        2
      );
    }

    if (outputType === "http" || outputType === "json") {
      return JSON.stringify(output, null, 2);
    }

    if (outputType === "excel" || outputType === "csv") {
      return selectedOutputFieldRows.map((field) => field.label).join(" | ");
    }

    if (outputType === "sql") {
      return selectedOutputFieldRows
        .map((field) => `${field.key.replace(/[^a-zA-Z0-9]+/g, "_")} NVARCHAR(MAX)`)
        .join("\n");
    }

    return JSON.stringify(
      {
        container: "integrations",
        path: `${environment}/${selectedCustomer?.customerId || "customer"}/${integrationName}.${outputType}`,
        fields: selectedOutputFieldRows.map((field) => field.key),
      },
      null,
      2
    );
  }, [
    selectedOutputFieldRows,
    outputType,
    environment,
    selectedCustomer,
    integrationName,
    httpMethod,
    httpUrl,
    httpAuthType,
    httpAuthValue,
    httpHeaders,
  ]);

  const selectedArrayPath = useMemo(() => payloadArrays[0]?.key || "", [payloadArrays]);

  const missingRequiredMappings = useMemo(
    () =>
      suggestedPayloadMappings.filter(
        (mapping) =>
          mapping.target.required &&
          mapping.target.path !== "branchId" &&
          mapping.target.path !== "customer.id" &&
          !mapping.source &&
          !mapping.constantValue
      ),
    [suggestedPayloadMappings]
  );

  const mappingHealthLabel =
    missingRequiredMappings.length === 0
      ? "Ready"
      : `${missingRequiredMappings.length} required fields missing`;

  const testCreateBooking = async () => {
    if (!mappedBookingPayload) {
      setTestBookingState({
        loading: false,
        status: "error",
        message: "Payload is invalid. Fix the JSON payload first.",
        responseBody: "",
      });
      return;
    }

    if (!selectedCustomerId) {
      setTestBookingState({
        loading: false,
        status: "error",
        message: "Kies eerst een customer voordat je de test booking verstuurt.",
        responseBody: "",
      });
      return;
    }

    if (!API_BASE_URL) {
      setTestBookingState({
        loading: false,
        status: "error",
        message: "API base URL ontbreekt. Zet VITE_API_BASE_URL in je .env.",
        responseBody: "",
      });
      return;
    }

    try {
      setTestBookingState({
        loading: true,
        status: "idle",
        message: "Test booking wordt verstuurd...",
        responseBody: "",
      });

      const payloadFilled: any = JSON.parse(JSON.stringify(mappedBookingPayload || {}));
      const now = new Date();
      const toIsoDate = (d: Date) => d.toISOString();
      const isEmpty = (val: any) =>
        val === null ||
        val === undefined ||
        (typeof val === "string" && val.trim() === "");

      if (isEmpty(payloadFilled.cargoReadyDate)) {
        const d = new Date(now);
        d.setDate(d.getDate() + 7);
        payloadFilled.cargoReadyDate = toIsoDate(d);
      }

      if (isEmpty(payloadFilled.targetedArrivalDate)) {
        const d = new Date(now);
        d.setDate(d.getDate() + 28);
        payloadFilled.targetedArrivalDate = toIsoDate(d);
      }

      if (isEmpty(payloadFilled.customerReference)) {
        payloadFilled.customerReference = `AUTO-${selectedCustomerId?.slice(0, 8) || "UNK"}-${Date.now()}`;
      }

      if (!payloadFilled.originPort) payloadFilled.originPort = {};
      if (isEmpty(payloadFilled.originPort.unLoCode)) {
        payloadFilled.originPort.unLoCode = "NLRTM";
      }

      if (!payloadFilled.destinationPort) payloadFilled.destinationPort = {};
      if (isEmpty(payloadFilled.destinationPort.unLoCode)) {
        payloadFilled.destinationPort.unLoCode = "DEHAM";
      }

      if (!Array.isArray(payloadFilled.containers) || payloadFilled.containers.length === 0) {
        payloadFilled.containers = [{ type: "20GP", quantity: 1 }];
      }

      const requestBody = {
        customerId: selectedCustomerId,
        customer: {
          id: selectedCustomerId,
        },
        branchId: DEFAULT_BRANCH_ID,
        retryPolicy: "none",
        mode: "test",
        payload: payloadFilled,
        _autoFilledDefaults: true,
      };

      const token = await getAccessToken();

      const res = await fetch(`${API_BASE_URL}${toAdminApiPath("/api/fa-jdx-create-booking")}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const text = await res.text();
      let responseBody = text;

      try {
        responseBody = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        responseBody = text;
      }

      if (!res.ok) {
        throw new Error(responseBody || `HTTP ${res.status}`);
      }

      setTestBookingState({
        loading: false,
        status: "success",
        message: "Test booking succesvol uitgevoerd.",
        responseBody,
      });
    } catch (error: any) {
      setTestBookingState({
        loading: false,
        status: "error",
        message: error?.message || "Test booking is mislukt.",
        responseBody: error?.message || "",
      });
    }
  };

  const selectedArrayVariables = useMemo(() => {
    if (!selectedArrayPath) return [];
    return payloadVariables.filter((variable) =>
      variable.key.startsWith(`${selectedArrayPath}[].`)
    );
  }, [payloadVariables, selectedArrayPath]);

  const variables = useMemo(() => {
    if (!selectedCustomer) return [];

    return [
      {
        key: "customerName",
        label: "Customer Name",
        value: selectedCustomer.customerName,
        source: "customer-config",
      },
      {
        key: "customerId",
        label: "Customer ID",
        value: selectedCustomer.customerId,
        source: "customer-config",
      },
      {
        key: "entityCode",
        label: "Entity Code",
        value: selectedCustomer.entityCode,
        source: "customer-config",
      },
      {
        key: "targetTable",
        label: "Target SQL Table",
        value: selectedCustomer.targetTable,
        source: "customer-config",
      },
      {
        key: "dataLoaderFunction",
        label: "Data Loader Function",
        value: selectedCustomer.dataLoaderFunction,
        source: "customer-config",
      },
      {
        key: "environment",
        label: "Environment",
        value: environment,
        source: "flow-context",
      },
      {
        key: "integrationName",
        label: "Integration Name",
        value: integrationName,
        source: "flow-context",
      },
      {
        key: "triggerType",
        label: "Trigger Type",
        value: triggerType,
        source: "flow-context",
      },
    ];
  }, [selectedCustomer, environment, integrationName, triggerType]);

  const generatedNames = useMemo(() => {
    if (!selectedCustomer) return null;

    const cleanIntegration =
      integrationName
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || "integration";

    const entity = selectedCustomer.entity;

    return {
      resourceGroup: `rg-jdx-${entity}-${cleanIntegration}-${environment}`,
      logicApp: `la-jdx-${entity}-${cleanIntegration}-${environment}`,
      functionApp: `func-jdx-${entity}-loader-${environment}`,
      storage: `stjdx${entity.replace(/-/g, "")}${cleanIntegration.replace(/-/g, "")}${environment}01`,
      appInsights: `appi-jdx-${entity}-${cleanIntegration}-${environment}`,
      keyVault: `kv-jdx-${entity}-${environment}`,
    };
  }, [selectedCustomer, integrationName, environment]);

  const defaultFlowSteps: FlowStep[] = [
    {
      id: "trigger",
      actionType: getFlowActionType(getTriggerTitle(triggerType)),
      title: getTriggerTitle(triggerType),
      subtitle: getTriggerSubtitle(triggerType),
      type: "Trigger",
      source: "default",
      accent: "border-cyan-400/70 bg-cyan-500/10 text-cyan-200",
    },
    {
      id: "select-customer",
      actionType: "jdx.select-customer",
      title: "Select Customer",
      subtitle: selectedCustomer?.customerName || "Select a customer",
      type: "JDX",
      source: "default",
      accent: "border-violet-400/70 bg-violet-500/10 text-violet-200",
    },
    {
      id: "booking",
      actionType:
        targetPayloadType === "shipment-booking"
          ? "jdx.create-shipment-booking"
          : "jdx.data-loader",
      title:
        targetPayloadType === "shipment-booking"
          ? "Create Shipment Booking"
          : "JDX Data Loader",
      subtitle:
        targetPayloadType === "shipment-booking"
          ? "POST mapped payload to the JDX booking API"
          : `Pass customerId ${selectedCustomer?.customerId || "TBD"} to loader`,
      type: "JDX",
      source: "default",
      accent: "border-teal-400/70 bg-teal-500/10 text-teal-200",
    },
  ];

  const visibleDefaultFlowSteps = defaultFlowSteps.filter(
    (step) => step.id === "trigger" || !hiddenDefaultStepIds.includes(step.id)
  );

  const flowSteps = [...visibleDefaultFlowSteps, ...customSteps];

  const selectedStep =
    flowSteps.find((step) => step.id === selectedStepId) || flowSteps[0];

  const selectedBlockVariables = useMemo(() => {
    switch (selectedStep?.actionType) {
      case "trigger.http":
        return [
          {
            key: "method",
            label: "Method",
            value: "POST",
            source: "http-trigger",
          },
          {
            key: "payload",
            label: "Payload",
            value: payloadIsValid ? "Valid JSON" : "Invalid JSON",
            source: "http-trigger",
          },
          ...payloadVariables,
        ];

      case "trigger.email":
        return [
          {
            key: "email.subject",
            label: "Subject",
            value: "{{email.subject}}",
            source: "email-trigger",
          },
          {
            key: "email.from",
            label: "From",
            value: "{{email.from}}",
            source: "email-trigger",
          },
          {
            key: "email.body",
            label: "Body",
            value: "{{email.body}}",
            source: "email-trigger",
          },
          {
            key: "email.attachments",
            label: "Attachments",
            value: "[array]",
            source: "email-trigger",
          },
        ];

      case "trigger.recurrence":
        return [
          {
            key: "recurrence.frequency",
            label: "Frequency",
            value: "Minute",
            source: "recurrence-trigger",
          },
          {
            key: "recurrence.interval",
            label: "Interval",
            value: "5",
            source: "recurrence-trigger",
          },
        ];

      case "jdx.select-customer":
        return variables.filter((v) =>
          ["customerName", "customerId", "entityCode"].includes(v.key)
        );

      case "jdx.create-shipment-booking":
        return [
          {
            key: "endpoint",
            label: "Endpoint",
            value: "POST /shipment-bookings",
            source: "jdx-api",
          },
          {
            key: "auth",
            label: "Authorization",
            value: "ApiKey",
            source: "jdx-api",
          },
          ...variables.filter((v) =>
            ["customerId", "entityCode", "environment"].includes(v.key)
          ),
          ...payloadVariables,
        ];

      case "jdx.data-loader":
        return variables.filter((v) =>
          ["customerId", "entityCode", "targetTable", "dataLoaderFunction"].includes(v.key)
        );

      case "output.http-response":
      case "output.http-push":
      case "output.excel":
      case "output.json":
      case "output.csv":
      case "output.blob":
        return [...variables, ...payloadVariables];

      case "data.sql-writer":
        return variables.filter((v) =>
          ["targetTable", "customerId", "entityCode"].includes(v.key)
        );

      default:
        return [...variables, ...payloadVariables];
    }
  }, [selectedStep?.actionType, payloadIsValid, payloadVariables, variables]);

  const mappingRows =
    triggerType === "http" && payloadVariables.length > 0
      ? payloadVariables
          .filter((variable) => !variable.value.startsWith("[array:"))
          .slice(0, 12)
          .map((variable) => [
            variable.key.replace(/^payload\./, ""),
            variable.key
              .replace(/^payload\./, "")
              .replace(/\[\]\./g, ".")
              .replace(/\[\d+\]\./g, ".")
              .replace(/\./g, "_"),
            "Auto",
          ])
      : [
          ["bookingNumber", "booking_reference", "98%"],
          ["customer.name", "customer_name", "95%"],
          ["customer.code", "customer_code", "94%"],
        ];

  const addBlock = (block: string) => {
    if (block === "When a new email arrives") {
      setTriggerType("email");
      setSelectedStepId("trigger");
      setActiveTab("properties");
      return;
    }

    if (block === "Recurrence") {
      setTriggerType("recurrence");
      setSelectedStepId("trigger");
      setActiveTab("properties");
      return;
    }

    if (block === "HTTP") {
      setTriggerType("http");
      setSelectedStepId("trigger");
      setActiveTab("properties");
      return;
    }

    const meta = getBlockMeta(block);
    const actionType = getFlowActionType(block);

    const outputBlockMap: Record<string, OutputType> = {
      "HTTP Response": "http",
      "HTTP Push": "http-push",
      "Excel Export": "excel",
      "JSON Output": "json",
      "CSV Export": "csv",
      "Blob Storage": "blob",
    };

    if (outputBlockMap[block]) {
      setOutputType(outputBlockMap[block]);
    }

    const newStep: FlowStep = {
      id: `custom-${Date.now()}-${actionType}`,
      actionType,
      title: block,
      subtitle:
        block === "JDX Data Loader"
          ? `Uses customerId ${selectedCustomer?.customerId || "TBD"}`
          : block === "Create Shipment Booking"
            ? "POST mapped payload to the JDX booking API"
            : "Custom block added to this integration flow",
      type: meta.type,
      source: "custom",
      accent: meta.accent,
    };

    setCustomSteps((prev) => [...prev, newStep]);
    setSelectedStepId(newStep.id);
    setActiveTab("properties");
  };

  const removeSelectedStep = () => {
    if (!selectedStep) return;

    if (selectedStep.id === "trigger") return;

    if (selectedStep.source === "custom") {
      setCustomSteps((prev) => prev.filter((step) => step.id !== selectedStep.id));
      setSelectedStepId("trigger");
      return;
    }

    setHiddenDefaultStepIds((prev) =>
      prev.includes(selectedStep.id) ? prev : [...prev, selectedStep.id]
    );

    setSelectedStepId("trigger");
  };

  const formatPayload = () => {
    try {
      setPayload(JSON.stringify(JSON.parse(payload), null, 2));
    } catch {
      // Keep invalid JSON so users can correct it.
    }
  };

  const isOutputStep = selectedStep?.actionType?.startsWith("output.");

  return {
    customers,
    customersLoading,
    customersError,
    selectedCustomer,
    selectedCustomerId,
    selectedEntity,
    environment,
    setEnvironment,
    customerSearch,
    setCustomerSearch,
    customerPickerOpen,
    setCustomerPickerOpen,
    filteredCustomers,
    selectCustomer,
    integrationName,
    setIntegrationName,
    triggerType,
    setTriggerType,
    payload,
    setPayload,
    activeTab,
    setActiveTab,
    selectedStepId,
    setSelectedStepId,
    customSteps,
    setCustomSteps,
    hiddenDefaultStepIds,
    setHiddenDefaultStepIds,
    dynamicContentOpen,
    setDynamicContentOpen,
    payloadMappingEnabled,
    setPayloadMappingEnabled,
    inputFormat,
    setInputFormat,
    targetPayloadType,
    setTargetPayloadType,
    mapperModalOpen,
    setMapperModalOpen,
    manualMappings,
    setManualMappings,
    mappingTransformations,
    setMappingTransformations,
    mappingConstants,
    setMappingConstants,
    retryPolicy,
    setRetryPolicy,
    retryCount,
    setRetryCount,
    testBookingState,
    setTestBookingState,
    outputType,
    setOutputType,
    outputSchema,
    setOutputSchema,
    selectedOutputFields,
    setSelectedOutputFields,
    outputPreviewOpen,
    setOutputPreviewOpen,
    httpMethod,
    setHttpMethod,
    httpUrl,
    setHttpUrl,
    httpAuthType,
    setHttpAuthType,
    httpAuthValue,
    setHttpAuthValue,
    httpHeaders,
    setHttpHeaders,
    parsedPayload,
    payloadAnalysis,
    payloadVariables,
    payloadArrays,
    payloadIsValid,
    selectedTargetPayload,
    suggestedPayloadMappings,
    availableOutputFields,
    selectedOutputFieldRows,
    mappedBookingPayload,
    bookingPayloadPreview,
    outputPreview,
    selectedArrayPath,
    selectedArrayVariables,
    missingRequiredMappings,
    mappingHealthLabel,
    testCreateBooking,
    variables,
    generatedNames,
    defaultFlowSteps,
    visibleDefaultFlowSteps,
    flowSteps,
    selectedStep,
    selectedBlockVariables,
    mappingRows,
    addBlock,
    removeSelectedStep,
    formatPayload,
    isOutputStep,
  };
}
